import { useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import Box from "../../../Common/Box/Box";
import Chart from "../../../Common/Box/Chart";
import DetailIndicator from "../../../Common/Box/DetailIndicator";
import Legend from "../../../Common/Box/Legend";
import { chartDownloadImageScaleFactor } from "../../../../constants";
import exportCharts from "../../../../export";
import theme from "../../../../theme";
import { capitalizePhrase, getTooltip } from "../../../../utilities";

const key = "APAC_CS_f7";
const number = 7;

const getMaximumValue = (data) => {
  let maximumValue = 0;

  for (let method of data) {
    for (let scenario of method.scenarios)
      for (let year of scenario.years) {
        maximumValue = Math.max(maximumValue, year.cost);
      }
  }

  return maximumValue;
};

const renameMethod = (method) => {
  switch (method) {
    case "MVA":
      return "Surgical: Vacuum aspiration";

    case "Medical":
      return "Medical:<br />All";

    case "Miso":
      return "Miso only";

    case "Mife+miso":
      return "Miso & mife";

    default:
      return method;
  }
};

const getSeriesColor = (name, theme) => {
  switch (name) {
    case "Maintain":
      return theme.palette.scenarios.scenario1;

    case "Shift in safety":
      return theme.palette.scenarios.scenario2;

    case "Shift in safety & method":
      return theme.palette.scenarios.scenario3;

    default:
      return "black";
  }
};

const options = (data, props) => (theme) => ({
  chart: {
    type: "spline",
    height: props.height,
    backgroundColor: null,
    width: props.showYAxis ? 236 : 180,
  },
  title: {
    text: data.method,
    align: "center",
    x: props.showYAxis ? 28 : undefined,
    style: {
      fontSize: props.titleFontSize ?? "18px",
    },
  },
  xAxis: {
    type: "datetime",
    min: 2017,
    max: 2032,
    tickPositions: [2019, 2025, 2030],
    tickLength: 0,
    labels: {
      formatter: function () {
        return [2019, 2025, 2030].includes(this.value) ? this.value : null;
      },
      style: {
        color: theme.figure.axis.secondary,
        fontSize: theme.figure.axis.fontSize,
      },
    },
  },
  yAxis: {
    title: undefined,
    min: 0,
    max: props.maximumValue,
    labels: {
      enabled: props.showYAxis,
      formatter: function () {
        return "$" + this.axis.defaultLabelFormatter.call(this);
      },
      style: {
        color: theme.figure.axis.secondary,
        fontSize: theme.figure.axis.fontSize,
      },
    },
  },
  plotOptions: {
    series: {
      lineWidth: 3,
      marker: {
        symbol: "circle",
        lineWidth: 3,
        fillColor: "white",
      },
      zoneAxis: "x",
      zones: [
        {
          value: 2025,
        },
        {
          dashStyle: "shortDot",
        },
      ],
    },
  },
  legend: {
    enabled: false,
  },
  tooltip: {
    backgroundColor: "white",
    useHTML: true,
    outside: true,
    formatter: function () {
      return getTooltip("Cost", this.y, undefined, {
        prefix: "$",
      });
    },
    style: {
      fontSize: "0.875rem",
      color: theme.palette.tooltip.primary,
    },
  },
  exporting: {
    enabled: false,
  },
  series: data.scenarios.map((scenario) => {
    const color = getSeriesColor(scenario.name, theme);

    return {
      ...scenario,
      color,
      marker: {
        lineColor: color,
      },
      data: scenario.years.map((year) => [year.year, year.cost]),
    };
  }),
});

const useStyles = makeStyles((theme) => ({
  content: {
    alignItems: "center",
    display: "flex",
  },
  detailIndicator: {
    margin: theme.spacing(0, 1),
  },
  facetPanel: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    margin: theme.spacing(2, 0),
  },
  legend: {
    marginLeft: theme.spacing(4),
  },
}));

const ApacCsF7 = (props) => {
  const highchartRefs = [useRef(), useRef(), useRef(), useRef()];

  const classes = useStyles();

  const yAxisMaximum = getMaximumValue(props.data.slice(0, 2)) * 1.02;
  const reusableYAxisMaximum = getMaximumValue(props.data.slice(2, 4)) * 1.02;

  return (
    <Box
      key={key}
      caption={`Figure ${number}`}
      title={`Change in Cost of Abortion and PAC Supplies in ${capitalizePhrase(
        props.regionName
      )}`}
      subtitle="By Future Scenario and Method, 2019-2030"
      onDownloadPng={() => {
        exportCharts(
          {
            chart1: highchartRefs[0].current,
            chart2: highchartRefs[1].current,
            chart3: highchartRefs[2].current,
            chart4: highchartRefs[3].current,
          },
          {
            filename: key,
            scale: chartDownloadImageScaleFactor,
          }
        );
      }}
    >
      <div className={classes.content}>
        <div className={classes.facetPanel}>
          {props.data
            .map((method) => ({
              ...method,
              method: renameMethod(method.name),
            }))
            .slice(0, 2)
            .map((method, index) => (
              <Chart
                key={method.name}
                highchartRef={highchartRefs[index]}
                options={options(method, {
                  height: 350,
                  maximumValue: yAxisMaximum,
                  showYAxis: index === 0,
                })}
              />
            ))}

          <div className={classes.detailIndicator}>
            <DetailIndicator />
          </div>

          {props.data
            .map((method) => ({
              ...method,
              method: renameMethod(method.name),
            }))
            .slice(2, 4)
            .map((method, index) => (
              <div key={method.name} className={classes.facet}>
                <Chart
                  highchartRef={highchartRefs[index + 2]}
                  options={options(method, {
                    height: 280,
                    maximumValue: reusableYAxisMaximum,
                    showYAxis: index === 0,
                    titleFontSize: "0.875rem",
                  })}
                />
              </div>
            ))}
        </div>

        <div className={classes.legend}>
          <Legend
            series={[
              {
                name: "3: Shift safety profile & method mix",
                color: getSeriesColor("Shift in safety & method", theme),
              },
              {
                name: "2: Shift safety profile",
                color: getSeriesColor("Shift in safety", theme),
              },
              {
                name: "1: Maintain safety profile & method mix",
                color: getSeriesColor("Maintain", theme),
              },
            ]}
            markerStyle="point"
          />
        </div>
      </div>
    </Box>
  );
};

ApacCsF7.propTypes = {
  regionName: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      scenarios: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          years: PropTypes.arrayOf(
            PropTypes.shape({
              year: PropTypes.number.isRequired,
              cost: PropTypes.number.isRequired,
            })
          ).isRequired,
        })
      ),
    }).isRequired
  ),
};

export default ApacCsF7;
